import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { fmtDateTime, fmtMoney } from "@/lib/format";
import { InvoiceStatusBadge } from "@/components/invoices/InvoiceStatusBadge";
import { LinesEditor, toEditLines, fromEditLines, lineCents } from "@/components/invoices/LinesEditor";
import { InvoiceActions } from "@/components/invoices/InvoiceActions";

const formOf = (inv) => ({
  client_name: inv.client_name || "", client_email: inv.client_email || "", client_address: inv.client_address || "",
  issue_date: inv.issue_date, due_date: inv.due_date, gst: !!inv.gst, notes: inv.notes || "", rows: toEditLines(inv.lines),
});

const Totals = ({ rows, gst }) => {
  const sub = rows.reduce((s, r) => s + lineCents(r), 0);
  const g = gst ? Math.round(sub * 0.1) : 0;
  const Row = ({ k, v, bold, testId }) => (
    <div className={`flex justify-between gap-8 ${bold ? "font-bold text-base border-t border-border pt-1.5" : "text-muted-foreground"}`}><span>{k}</span><span className="tabular-nums" data-testid={testId}>{fmtMoney(v)}</span></div>
  );
  return (
    <div className="ml-auto w-full sm:w-64 space-y-1 text-sm">
      <Row k={gst ? "Subtotal (ex GST)" : "Subtotal"} v={sub} testId="invoice-subtotal" />
      {gst && <Row k="GST (10%)" v={g} testId="invoice-gst" />}
      <Row k="Total" v={sub + g} bold testId="invoice-total" />
    </div>
  );
};

const Field = ({ label, children }) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;

const InvoiceForm = ({ f, set, locked, items }) => (
  <div className="space-y-4">
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Client"><Input value={f.client_name} disabled={locked} onChange={(e) => set("client_name", e.target.value)} data-testid="invoice-client-name" /></Field>
      <Field label="Client email (invoice goes here)"><Input type="email" value={f.client_email} disabled={locked} onChange={(e) => set("client_email", e.target.value)} data-testid="invoice-client-email" /></Field>
      <Field label="Billing address"><Input value={f.client_address} disabled={locked} onChange={(e) => set("client_address", e.target.value)} data-testid="invoice-client-address" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Issue date"><Input type="date" value={f.issue_date} disabled={locked} onChange={(e) => set("issue_date", e.target.value)} data-testid="invoice-issue-date" /></Field>
        <Field label="Due date"><Input type="date" value={f.due_date} min={f.issue_date} disabled={locked} onChange={(e) => set("due_date", e.target.value)} data-testid="invoice-due-date" /></Field>
      </div>
    </div>
    <LinesEditor rows={f.rows} setRows={(rows) => set("rows", rows)} items={items} disabled={locked} />
    <div className="flex flex-col sm:flex-row sm:items-start gap-4 pt-2">
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.gst} disabled={locked} onCheckedChange={(v) => set("gst", v)} data-testid="invoice-gst-toggle" /> Add 10% GST</label>
      <Totals rows={f.rows} gst={f.gst} />
    </div>
    <Field label="Note on the invoice"><Textarea rows={2} value={f.notes} disabled={locked} onChange={(e) => set("notes", e.target.value)} data-testid="invoice-notes" /></Field>
  </div>
);

export const InvoiceDialog = ({ invoiceId, onClose, onChanged }) => {
  const [inv, setInv] = useState(null);
  const [f, setF] = useState(null);
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!invoiceId) return;
    setInv(null);
    Promise.all([api.invoice(invoiceId), api.invoiceSettings().catch(() => ({ items: [] }))])
      .then(([i, s]) => { setInv(i); setF(formOf(i)); setItems(s.items || []); })
      .catch(() => { toast.error("Could not load invoice"); onClose(); });
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const locked = !inv || ["paid", "void"].includes(inv.status);
  const dirty = inv && f && JSON.stringify(formOf(inv)) !== JSON.stringify(f);
  const updated = (i) => { setInv(i); setF(formOf(i)); onChanged?.(); };

  const save = async () => {
    const lines = fromEditLines(f.rows);
    if (lines.some((l) => !l.description || !(l.qty > 0) || Number.isNaN(l.unit_c))) { toast.error("Each line needs a description, a quantity and a price"); return null; }
    const { rows, ...rest } = f;
    try { const i = await api.updateInvoice(inv.id, { ...rest, lines }); updated(i); return i; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save invoice"); return null; }
  };

  return (
    <Dialog open={!!invoiceId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="invoice-dialog">
        {!inv || !f ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-xl flex items-center gap-3" data-testid="invoice-dialog-title">
                {inv.number ? `Invoice ${inv.number}` : "Draft invoice"} <InvoiceStatusBadge status={inv.display_status} testId="invoice-dialog-status" />
              </DialogTitle>
              <DialogDescription>{inv.job_name}{inv.job_address ? ` · ${inv.job_address}` : ""}</DialogDescription>
            </DialogHeader>
            <InvoiceForm f={f} set={set} locked={locked} items={items} />
            <InvoiceActions inv={inv} dirty={dirty} save={save} onUpdated={updated} onDeleted={() => { onChanged?.(); onClose(); }} />
            {inv.history?.length > 0 && (
              <div className="border-t border-border pt-3 space-y-1" data-testid="invoice-history">
                {[...inv.history].reverse().slice(0, 6).map((h, i) => (
                  <p key={i} className="text-xs text-muted-foreground"><span className="text-foreground">{h.what}</span> · {h.by} · {fmtDateTime(h.at)}</p>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
